// Hydra analytics client — wraps /api/analytics/* on the Hydra Fastify server.
// Auth is a single shared bearer token (separate from the consumer WS bearer).
// Spec: hydra/docs/G1_ANALYTICS_INTEGRATION.md.

import { toHydraGameKey } from './analyticsGameKey';

const BASE = import.meta.env.VITE_HYDRA_ANALYTICS_URL as string | undefined;
const TOKEN = import.meta.env.VITE_HYDRA_ANALYTICS_TOKEN as string | undefined;
// Unset preserves the existing full analytics contract. A Core game-state
// source is not a warehouse; its explicit capability must not advertise one.
const CAPS = (import.meta.env.VITE_HYDRA_ANALYTICS_CAPS as string | undefined)?.trim() ?? '';
const CAPABILITIES = new Set(CAPS.split(',').map(s => s.trim()).filter(Boolean));
function hasCapability(capability: 'warehouse' | 'game-states'): boolean {
  return typeof BASE === 'string' && BASE.length > 0
    && (CAPS === '' || CAPABILITIES.has('all') || CAPABILITIES.has(capability));
}

export function hasAnalytics(): boolean {
  return hasCapability('warehouse');
}

export function hasGameStateAnalytics(): boolean { return hasCapability('game-states'); }

export class AnalyticsError extends Error {
  status: number;
  code?: string;
  constructor(message: string, status: number, code?: string) {
    super(message);
    this.name = 'AnalyticsError';
    this.status = status;
    this.code = code;
  }
}

export async function fetchAnalytics<T>(path: string, signal?: AbortSignal): Promise<T> {
  if (!BASE) throw new AnalyticsError('VITE_HYDRA_ANALYTICS_URL not set', 0, 'NO_BASE');
  const relative = path.startsWith('/') ? path : `/${path}`;
  const gameStates = relative.split('?')[0] === '/api/analytics/game-states';
  if (!(gameStates ? hasGameStateAnalytics() : hasAnalytics())) {
    throw new AnalyticsError(gameStates ? 'Game state analytics unavailable from this source'
      : 'Warehouse analytics unavailable from this source', 0, 'CAPABILITY_UNAVAILABLE');
  }
  const url = `${BASE.replace(/\/$/, '')}${relative}`;
  const headers: HeadersInit = TOKEN ? { Authorization: `Bearer ${TOKEN}` } : {};
  const res = await fetch(url, { signal, headers });
  if (!res.ok) {
    let code: string | undefined;
    let message = `${res.status} ${res.statusText}`;
    try {
      const body = (await res.json()) as { error?: string; code?: string };
      if (body?.error) message = body.error;
      if (body?.code) code = body.code;
    } catch {
      // non-JSON error body — fall back to status line
    }
    throw new AnalyticsError(message, res.status, code);
  }
  return (await res.json()) as T;
}

// ─── Response shapes ────────────────────────────────────────────────

export interface AnalyticsListEnvelope<T> {
  items: T[];
  total: number;
  limit: number;
  offset: number;
}

export interface AnalyticsAggregateEnvelope<T> {
  data: T;
  generatedAt: string;
}

export interface PgStatsTable {
  name: string;
  estimatedRows: number;
  totalBytes: number;
  partitions: number;
}

export interface PgStatsPartition {
  name: string;
  parent: string;
  rows: number;
  size: string;
}

export interface PgStatsBuffer {
  pending: number;
  flushed: number;
  lastFlushAt: string | null;
}

export interface PgStatsUptime {
  startedAt: string;
  uptimeMs: number;
  growthBytes: number;
  growthPerHour: number;
}

export interface PgStats {
  databaseSize: string;
  databaseBytes: number;
  tables: PgStatsTable[];
  partitions: PgStatsPartition[];
  buffer: PgStatsBuffer;
  uptime: PgStatsUptime;
}

export type PgStatsResponse = AnalyticsAggregateEnvelope<PgStats>;

export interface GameSummary {
  gameKey: string;
  date: string;
  teams: string[];
  sport: string;
  markets: Array<{
    marketType: string;
    tickCount: number;
    sources: number;
    firstTick: string;
    lastTick: string;
  }>;
  trades: Array<{ source: string; tradeCount: number; totalSize: number }>;
}

export interface GamesResponse extends AnalyticsListEnvelope<GameSummary> {
  date: string;
}

export interface TradeRow {
  // Hydra-receive-time (ms precision, wall-clock at decode). For
  // Polymarket on-chain rows this is offset from the real match time by
  // ~2s steady-state and up to ~35min during chain-listener backfills.
  // Use `block_ts` when present and fall back to `ts` only for legacy
  // rows / Kalshi rows / lookup failures. See [[project_hydra_ask_poly_trade_ts_skew]].
  ts: string;
  // Canonical on-chain block time (1s precision, from eth_getBlock).
  // Populated for Polymarket on-chain rows ingested after the 2026-05-15
  // hydra fix; NULL on Kalshi rows, legacy rows, and rows where the
  // eth_getBlock lookup failed.
  block_ts: string | null;
  ticker: string;
  source: string;
  game_key: string | null;
  market_type: string | null;
  line_key: string | null;
  sport: string | null;
  side: string;
  price: number;
  size: number;
  external_trade_id: string | null;
  asset_id: string | null;
  market_id: string | null;
  maker_address: string | null;
  taker_address: string | null;
  trader_side: string | null;
  tx_hash: string | null;
  count: number;
  taker_side: string | null;
}

export interface TradesResponse extends AnalyticsListEnvelope<TradeRow> {
  gameKey: string;
}

export type RecentTradesResponse = AnalyticsListEnvelope<TradeRow>;

export interface MarketSummary {
  ticker: string;
  source: string;
  sport: string | null;
  gameKey: string | null;
  marketType: string | null;
  tradeCount: number;
  totalSize: number;
  avgPrice: number;
  firstTradeAt: string;
  lastTradeAt: string;
}

export interface TradeSummaryResponse extends AnalyticsListEnvelope<MarketSummary> {
  date: string;
}

export interface HourlyBucket {
  hour: number;
  tradeCount: number;
  totalSize: number;
}

export interface HourlyActivity {
  date: string;
  sport: string | null;
  source: string | null;
  hours: HourlyBucket[];
}

export type HourlyActivityResponse = AnalyticsAggregateEnvelope<HourlyActivity>;

export interface SizeHistogramBin {
  lower: number;
  upper: number;
  count: number;
}

export interface SizeDistribution {
  date: string;
  buckets: number;
  totalTrades: number;
  min: number;
  max: number;
  median: number;
  bins: SizeHistogramBin[];
}

export type SizeDistributionResponse = AnalyticsAggregateEnvelope<SizeDistribution>;

export interface IntervalRange {
  label: string;
  lowerMs: number;
  upperMs: number | null;
  count: number;
}

export interface IntervalHourRow {
  hour: number;
  ranges: IntervalRange[];
}

export interface TradeIntervals {
  date: string;
  sport: string | null;
  source: string | null;
  hours: IntervalHourRow[];
}

export type TradeIntervalsResponse = AnalyticsAggregateEnvelope<TradeIntervals>;

export type LeaderboardWindow = '1d' | '7d' | '30d' | '90d' | 'all';
export type LeaderboardSort = 'winRate' | 'avgGainUsd' | 'gamesPlayed' | 'totalPnlUsd';

export interface OperatorLeaderboardRow {
  address: string;
  source: 'polymarket';
  gamesPlayed: number;
  wins: number;
  losses: number;
  winRate: number;
  totalPnlUsd: number;
  avgGainUsd: number;
  totalVolumeUsd: number;
}

export interface OperatorLeaderboardMeta {
  source: 'polymarket';
  window: LeaderboardWindow;
  minGames: number;
  sort: LeaderboardSort;
  order: 'desc' | 'asc';
}

export interface OperatorLeaderboardResponse
  extends AnalyticsListEnvelope<OperatorLeaderboardRow> {
  meta: OperatorLeaderboardMeta;
}

export interface CascadeOperator {
  id: string;
  label: string;
  volume: number;
  count: number;
  firstActiveAt?: string;
  lastActiveAt?: string;
}

export interface CascadeTrade {
  id: string;
  t: string; // ISO timestamp
  operatorId: string;
  marketId?: string | null;
  priceCents: number;
  size: number;
  side: 'BUY' | 'SELL' | string;
}

export interface CascadeRelationship {
  sourceTradeId: string;
  targetTradeId: string;
  lagMs: number;
  strength: number; // 0..1
}

export interface CascadeData {
  gameKey: string;
  operators: CascadeOperator[];
  trades: CascadeTrade[];
  relationships: CascadeRelationship[];
}

export type CascadeResponse = AnalyticsAggregateEnvelope<CascadeData>;

// ─── Typed wrappers ─────────────────────────────────────────────────

export function getPgStats(signal?: AbortSignal): Promise<PgStatsResponse> {
  return fetchAnalytics<PgStatsResponse>('/api/analytics/pg-stats', signal);
}

// ─── Game states (Unabated, batched) ────────────────────────────────

export interface UnabatedGameState {
  statusId: number;        // 1=scheduled, 2=live, 3=final
  awayScore: number;
  homeScore: number;
  periodTypeId: number | null;
  periodLabel: string | null;
  gameClock: string | null;
  lastUpdateMs: number;
  finalizedAtMs: number;
  /** ISO 8601 UTC event start — Unabated's canonical scheduled start time
   *  for the game. Always populated when hydra returns a non-null
   *  state (CachedGameState.eventStart is non-nullable upstream).
   *  Wired into marketStore's _startTimeMsByGameKey cache by the
   *  gameStates poll so spreads/totals and Poly-less games inherit
   *  the same start-time signal via canonical key. */
  eventStart: string;
  /** Full-game (periodTypeId === 1) Unabated consensus snapshot. Null
   *  when the consensus feed hasn't reported on this game yet (pre-
   *  game niche sport, network blip). Drives the panel's "U" row;
   *  awayTeam/homeTeam carry Unabated's real away/home assignment so
   *  callers can align against the local GameRow's awayCode (the
   *  canonical gameKey is alpha-sorted and doesn't always match). */
  consensus: {
    awayFairProbCents: number;
    homeFairProbCents: number;
    awayTeam: string;
    homeTeam: string;
    periodTypeId: number;
    contributingBooks: number;
    lastUpdateMs: number;
  } | null;
}

export interface GameStatesResponse {
  states: Record<string, UnabatedGameState | null>;
  serverTs: number;
}

const GAME_STATES_BATCH_CAP = 50;

/**
 * Batched gameState fetch from hydra's Unabated cache. Pass up to
 * `GAME_STATES_BATCH_CAP` canonical gameKeys; hydra returns the
 * statusId / score / period for each, or `null` for keys it doesn't
 * have. Used to drive "finished games drop to bottom" + inline live
 * scores on the value dashboard without spawning a per-game SSE.
 *
 * Caller is responsible for chunking when the key set exceeds the
 * cap — `useGameStates` does that.
 */
export function getGameStates(
  gameKeys: string[],
  signal?: AbortSignal,
): Promise<GameStatesResponse> {
  if (gameKeys.length === 0) {
    return Promise.resolve({ states: {}, serverTs: Date.now() });
  }
  if (gameKeys.length > GAME_STATES_BATCH_CAP) {
    return Promise.reject(
      new AnalyticsError(
        `getGameStates: max ${GAME_STATES_BATCH_CAP} keys per request, got ${gameKeys.length}`,
        0,
        'BATCH_OVERFLOW',
      ),
    );
  }
  const qs = new URLSearchParams({ gameKeys: gameKeys.join(',') });
  return fetchAnalytics<GameStatesResponse>(
    `/api/analytics/game-states?${qs}`,
    signal,
  );
}

export { GAME_STATES_BATCH_CAP };

export function getGames(
  params: { date?: string; tz?: string } = {},
  signal?: AbortSignal,
): Promise<GamesResponse> {
  const qs = new URLSearchParams();
  if (params.date) qs.set('date', params.date);
  if (params.tz) qs.set('tz', params.tz);
  const suffix = qs.toString() ? `?${qs}` : '';
  return fetchAnalytics<GamesResponse>(`/api/analytics/games${suffix}`, signal);
}

export function getTrades(
  params: { gameKey: string; marketType?: string; limit?: number; offset?: number },
  signal?: AbortSignal,
): Promise<TradesResponse> {
  const qs = new URLSearchParams();
  qs.set('gameKey', params.gameKey);
  if (params.marketType) qs.set('marketType', params.marketType);
  if (params.limit != null) qs.set('limit', String(params.limit));
  if (params.offset != null) qs.set('offset', String(params.offset));
  return fetchAnalytics<TradesResponse>(`/api/analytics/trades?${qs}`, signal);
}

function buildQs(params: Record<string, string | number | undefined>): string {
  const qs = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v == null || v === '') continue;
    qs.set(k, String(v));
  }
  return qs.toString() ? `?${qs}` : '';
}

export function getRecentTrades(
  params: { sport?: string; source?: string; limit?: number } = {},
  signal?: AbortSignal,
): Promise<RecentTradesResponse> {
  return fetchAnalytics<RecentTradesResponse>(`/api/analytics/recent-trades${buildQs(params)}`, signal);
}

export function getTradeSummary(
  params: { date?: string; sport?: string; source?: string; limit?: number } = {},
  signal?: AbortSignal,
): Promise<TradeSummaryResponse> {
  return fetchAnalytics<TradeSummaryResponse>(`/api/analytics/trade-summary${buildQs(params)}`, signal);
}

export function getHourlyActivity(
  params: { date?: string; sport?: string; source?: string } = {},
  signal?: AbortSignal,
): Promise<HourlyActivityResponse> {
  return fetchAnalytics<HourlyActivityResponse>(`/api/analytics/hourly-activity${buildQs(params)}`, signal);
}

export function getSizeDistribution(
  params: { date?: string; sport?: string; source?: string; buckets?: number } = {},
  signal?: AbortSignal,
): Promise<SizeDistributionResponse> {
  return fetchAnalytics<SizeDistributionResponse>(`/api/analytics/size-distribution${buildQs(params)}`, signal);
}

export function getTradeIntervals(
  params: { date?: string; sport?: string; source?: string } = {},
  signal?: AbortSignal,
): Promise<TradeIntervalsResponse> {
  return fetchAnalytics<TradeIntervalsResponse>(`/api/analytics/trade-intervals${buildQs(params)}`, signal);
}

export function getOperatorLeaderboard(
  params: {
    window?: LeaderboardWindow;
    minGames?: number;
    sort?: LeaderboardSort;
    order?: 'desc' | 'asc';
    limit?: number;
    offset?: number;
  } = {},
  signal?: AbortSignal,
): Promise<OperatorLeaderboardResponse> {
  return fetchAnalytics<OperatorLeaderboardResponse>(
    `/api/analytics/operators/leaderboard${buildQs({ ...params, source: 'polymarket' })}`,
    signal,
  );
}

// ─── Orderbook Depth ────────────────────────────────────────────────

export interface DepthLevel {
  price: number;    // cents
  size: number;     // size at this level only
  cumSize: number;  // cumulative from L1 through this level
}

export interface DepthTick {
  ts: number;           // Unix ms
  levels: DepthLevel[]; // exactly 5 entries; index 0 = L1 (tightest/best)
}

export interface DepthSeries {
  ticks: DepthTick[]; // oldest → newest
}

export interface OrderbookDepthResponse {
  ticker: string;
  windowSecs: number;
  sampledAt: number;
  yes: DepthSeries;
  no: DepthSeries;
}

export function getOrderbookDepth(
  params: { ticker: string; windowSecs?: number },
  signal?: AbortSignal,
): Promise<OrderbookDepthResponse> {
  return fetchAnalytics<OrderbookDepthResponse>(
    `/api/analytics/orderbook-depth${buildQs(params)}`,
    signal,
  );
}

// ─── Playoff series round label ─────────────────────────────────────
// Used by ValueDashboard's series-row badge. The series matching itself
// flows through the live WS feed (canonicalSeriesKey on every series
// frame, collapsed in marketStore) — no analytics fetch needed for that.

const ROUND_LABELS: Record<'NBA' | 'NHL' | 'MLB', Record<number, string>> = {
  NBA: { 1: 'Round 1', 2: 'Round 2', 3: 'Conf Finals', 4: 'Finals' },
  NHL: { 1: 'Round 1', 2: 'Round 2', 3: 'Conf Finals', 4: 'Stanley Cup' },
  MLB: { 1: 'Wild Card', 2: 'LDS', 3: 'LCS', 4: 'World Series' },
};

export function roundLabel(sport: string, round: number): string {
  if (round === 0) return '—';
  const upper = sport.toUpperCase() as 'NBA' | 'NHL' | 'MLB';
  const map = ROUND_LABELS[upper];
  if (map?.[round]) return map[round];
  return `R${round}`;
}

// ─── Cross-Market Spread ─────────────────────────────────────────────

export interface CrossMarketMarket {
  gameKey: string;
  awayCode: string;
  homeCode: string;
  sport: string;
}

export interface CrossMarketMarketsResponse {
  ok: boolean;
  date: string;
  items: CrossMarketMarket[];
  total: number;
}

export interface CrossMarketSpreadRow {
  ts: string;
  kalshiMid: number | null;
  polyMid: number | null;
  rawDelta: number | null;
  netDelta: number | null;
}

export interface CrossMarketSpreadResponse {
  ok: boolean;
  gameKey: string;
  side: string;
  resolution: string;
  rows: CrossMarketSpreadRow[];
}

export function getCrossMarketSpreadMarkets(
  params: { date?: string; sport?: string } = {},
  signal?: AbortSignal,
): Promise<CrossMarketMarketsResponse> {
  return fetchAnalytics<CrossMarketMarketsResponse>(
    `/api/analytics/cross-market-spread/markets${buildQs(params)}`,
    signal,
  );
}

export function getCrossMarketSpread(
  params: {
    gameKey: string;
    side: string;
    resolution?: string;
    from?: string;
    to?: string;
    limit?: number;
  },
  signal?: AbortSignal,
): Promise<CrossMarketSpreadResponse> {
  return fetchAnalytics<CrossMarketSpreadResponse>(
    `/api/analytics/cross-market-spread${buildQs(params)}`,
  );
}

// Sport prefix in the gameKey causes upstream errors — normalize to the
// canonical `date|teamA|teamB` (alpha-sorted) shape via toHydraGameKey.
export function getCascade(
  params: { gameKey: string },
  signal?: AbortSignal,
): Promise<CascadeResponse> {
  const normalized = toHydraGameKey(params.gameKey) ?? params.gameKey;
  return fetchAnalytics<CascadeResponse>(
    `/api/analytics/operators/cascade/${encodeURIComponent(normalized)}`,
    signal,
  );
}

export interface OperatorPlayByPlayFill {
  wallet: string;
  ts: number;
  priceCents: number;
  sizeUsd: number;
  side: 'yes' | 'no';
  txHash: string;
}

export interface OperatorPlayByPlayOperator {
  wallet: string;
  fillCount: number;
  totalSizeUsd: number;
  firstTs: number;
  lastTs: number;
}

export interface OperatorPlayByPlayResponse {
  fills: OperatorPlayByPlayFill[];
  operators: OperatorPlayByPlayOperator[];
  meta: {
    gameKey: string;
    windowFrom: number | null;
    windowTo: number | null;
    referenceTeam: string | null;
  };
}

export function getOperatorPlayByPlay(
  params: { gameKey: string },
  signal?: AbortSignal,
): Promise<OperatorPlayByPlayResponse> {
  const normalized = toHydraGameKey(params.gameKey) ?? params.gameKey;
  return fetchAnalytics<OperatorPlayByPlayResponse>(
    `/api/analytics/operators/play-by-play/${encodeURIComponent(normalized)}`,
    signal,
  );
}

// ─── StreamRift: quote-vs-trade (v04 port) ──────────────────────────

export interface QuoteVsTradePoint {
  ticker: string;
  source: 'kalshi' | 'polymarket';
  sport: string | null;
  ticks: number;
  trades: number;
}

export interface QuoteVsTradeData {
  points: QuoteVsTradePoint[];
  medianTicksPerTrade: { kalshi: number; polymarket: number };
  rowsScanned: number;
}

export type QuoteVsTradeResponse = AnalyticsAggregateEnvelope<QuoteVsTradeData>;

export function getQuoteVsTrade(
  params: { date?: string; sport?: string; source?: 'kalshi' | 'polymarket'; limit?: number } = {},
  signal?: AbortSignal,
): Promise<QuoteVsTradeResponse> {
  return fetchAnalytics<QuoteVsTradeResponse>(
    `/api/analytics/quote-trade-counts${buildQs(params)}`,
    signal,
  );
}

// ─── StreamRift: dislocation (v08 port) ─────────────────────────────

export interface DislocationHistBin {
  sport: string;
  bucketLowCents: number;
  count: number;
}

export interface DislocationLeaderboardRow {
  rank: number;
  gameKey: string;
  sport: string | null;
  ts: string;
  kalshiYesMid: number;
  polyYesMid: number;
  gapCents: number;
  feeAdjGapCents: number;
}

export interface DislocationData {
  polyFeeMedianBps: number;
  histogram: DislocationHistBin[];
  topDislocations: DislocationLeaderboardRow[];
  rowsScanned: number;
}

export type DislocationResponse = AnalyticsAggregateEnvelope<DislocationData>;

export function getDislocation(
  params: { date?: string; sport?: string; histogramBins?: number; leaderboardLimit?: number } = {},
  signal?: AbortSignal,
): Promise<DislocationResponse> {
  return fetchAnalytics<DislocationResponse>(
    `/api/analytics/dislocation${buildQs(params)}`,
    signal,
  );
}

// ─── StreamRift: wallet personality (v18 port) ──────────────────────

/** Subset of WalletAggregateRow the archetype classifier reads — kept narrow
 *  so the classifier doesn't depend on the full row shape. */
export interface WalletAtlasMetrics {
  totalNotional: number;
  makerShare: number;
  takerShare: number;
  nTrades: number;
  distinctSports: number;
  topSportShare: number;
  minTop3SportShare: number;
  avgTradeNotional: number;
}

export interface WalletAggregateRow extends WalletAtlasMetrics {
  wallet: string;
  topSport: string | null;
}

export interface WalletPersonalityData {
  wallets: WalletAggregateRow[];
  thresholds: {
    whaleTotalNotionalUsd: number;
    sniperAvgNotionalUsd: number;
  };
  rowsScanned: number;
}

export type WalletPersonalityResponse = AnalyticsAggregateEnvelope<WalletPersonalityData>;

export function getWalletPersonality(
  params: { from: string; to: string; sport?: string; minTrades?: number; limit?: number },
  signal?: AbortSignal,
): Promise<WalletPersonalityResponse> {
  return fetchAnalytics<WalletPersonalityResponse>(
    `/api/analytics/wallets/aggregates${buildQs(params)}`,
    signal,
  );
}

// ─── StreamRift: wallet trail (v19 port) ────────────────────────────

export interface WalletTrailServerEvent {
  ts: string;
  sport: string | null;
  side: 'yes' | 'no';
  role: 'taker' | 'maker';
  ticker: string;
  gameKey: string | null;
  priceCents: number;
  size: number;
  notional: number;
  takerSide: string | null;
}

export interface WalletTrailRowsData {
  wallet: string;
  events: WalletTrailServerEvent[];
  nFills: number;
  totalNotional: number;
  rowsScanned: number;
}

export type WalletTrailRowsResponse = AnalyticsAggregateEnvelope<WalletTrailRowsData>;

export function getWalletTrailRows(
  address: string,
  params: { from: string; to: string; sport?: string; limit?: number },
  signal?: AbortSignal,
): Promise<WalletTrailRowsResponse> {
  return fetchAnalytics<WalletTrailRowsResponse>(
    `/api/analytics/wallets/${encodeURIComponent(address)}/trades${buildQs(params)}`,
    signal,
  );
}
